import { AppContext } from '@/app/appContext';
import ProjectsList from '@/app/components/features/projects/ProjectsList';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderOrganizationMenu,
  HeaderSidebarTrigger,
  HeaderUserMenu,
} from '@/app/ui/components/header';
import { toastManager } from '@/app/ui/components/toast';
import { validator } from '@/app/validator';
import { Building06Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { createFileRoute } from '@tanstack/react-router';
import { usePostHog } from 'posthog-js/react';
import { use, useEffect } from 'react';
import { useTranslations } from 'use-intl';
import { z } from 'zod';

export const Route = createFileRoute('/_authed/(organization)/_layout/')({
  component: RouteComponent,
  validateSearch: validator(
    z.object({
      inviteAccepted: z.boolean().optional(),
      organizationName: z.string().optional(),
    }),
  ),
});

function RouteComponent() {
  const t = useTranslations();
  const { userMeta } = use(AppContext);
  const search = Route.useSearch();
  const inviteAccepted = search?.inviteAccepted;
  const organizationName = search?.organizationName;
  const posthog = usePostHog();

  useEffect(() => {
    if (userMeta && import.meta.env.VITE_POSTHOG_KEY) {
      posthog.identify(userMeta.id, {
        email: userMeta.email,
        name: `${userMeta.firstName} ${userMeta.lastName}`,
      });

      if (userMeta.organizationId && userMeta.organizationName) {
        posthog.group('organization', userMeta.organizationId, {
          name: userMeta.organizationName,
        });
      }
    }
  }, [userMeta, posthog]);

  useEffect(() => {
    if (inviteAccepted && organizationName) {
      const id = toastManager.add({
        type: 'success',
        title: t('pages.homepage.toasts.successfullyJoined'),
        description: t.rich('pages.acceptInvitation.alerts.youAreAMember', {
          organizationName,
          b: (chunks: any) => <b>{chunks}</b>,
        }),
        timeout: Infinity,
        actionProps: {
          children: t('general.dismiss'),
          onClick: () => toastManager.close(id),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteAccepted, organizationName]);

  return (
    <>
      <Header>
        <HeaderOrganizationMenu />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbPage>
                <HugeiconsIcon icon={Building06Icon} />
                {t('pages.homepage.breadcrumbs.projects')}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderUserMenu />
      </Header>
      <ProjectsList />
    </>
  );
}
